import { apiClient } from '@/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/**
 * Maps each supported notification key to the i18n segment under namespace `inAppNotifications`
 * (`officialQuarterlyPrice.{year}.{quarter}`).
 * When the server adds a new period, add the key here and add matching entries in `inAppNotifications.json`.
 */
const OFFICIAL_QUARTERLY_EUA_PERIOD_BY_NOTIFICATION_KEY = {
  'UPDATE_SIMULATIONS_OFFICIAL_EUA:2026-Q1': { year: '2026', quarter: 'Q1' },
  'UPDATE_SIMULATIONS_OFFICIAL_EUA:2026-Q2': { year: '2026', quarter: 'Q2' },
  'UPDATE_SIMULATIONS_OFFICIAL_EUA:2026-Q3': { year: '2026', quarter: 'Q3' },
} as const satisfies Record<
  string,
  {
    year: string
    quarter: string
  }
>

type KnownOfficialQuarterlyKey = keyof typeof OFFICIAL_QUARTERLY_EUA_PERIOD_BY_NOTIFICATION_KEY

export function getOfficialQuarterlyEuaPeriodFromNotificationKey(
  notificationKey: string
): { year: string; quarter: string } | null {
  if (
    !Object.prototype.hasOwnProperty.call(
      OFFICIAL_QUARTERLY_EUA_PERIOD_BY_NOTIFICATION_KEY,
      notificationKey
    )
  ) {
    return null
  }
  return OFFICIAL_QUARTERLY_EUA_PERIOD_BY_NOTIFICATION_KEY[
    notificationKey as KnownOfficialQuarterlyKey
  ]
}

export function isOfficialQuarterlyEuaNotificationKey(notificationKey: string): boolean {
  return getOfficialQuarterlyEuaPeriodFromNotificationKey(notificationKey) !== null
}

export type PendingInAppNotification = {
  notificationKey: string
}

type PendingInAppNotificationsResponse = {
  notifications: PendingInAppNotification[]
}

export function usePendingInAppNotifications(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['pendingInAppNotifications'],
    queryFn: async () => {
      const response = await apiClient.get<PendingInAppNotificationsResponse>(
        '/v1/user/in-app-notifications'
      )
      return response.data
    },
    enabled: options?.enabled ?? true
  })
}

export function useDismissInAppNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { notificationKey: string }) => {
      await apiClient.post(`/v1/user/in-app-notifications/dismiss`, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInAppNotifications'] })
    }
  })
}
